import React, { useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { Briefcase, HardHat, Save, Loader2, Check } from 'lucide-react';

import StepHeader from './StepHeader';
import SimulationStep from './SimulationStep';
import AutonomousSimulationStep from './AutonomousSimulationStep';
import ClientDataStep from './ClientDataStep';
import AddressesStep from './AddressesStep';
import ContactsStep from './ContactsStep';
import ScheduleStep from './ScheduleStep';
import DocumentsStep from './DocumentsStep';
import ReviewStep from './ReviewStep';
import type { AiReviewState } from './aiReview';
import StatementCheckCard from './StatementCheckCard';
import type { StatementCheckState } from './statementCheck';
import DraftsList from './DraftsList';
import { saveDraft, deleteDraft, deserializeDraft, type ContractDraft } from './drafts';

import type { ManualRegistrationData, ClientType, UploadedDocument } from './types';
import {
  emptyAddress, emptyContact, parseAddress,
  DOCUMENT_CATEGORIES, AUTONOMOUS_DOCUMENT_CATEGORIES,
  calculateLoanDetails, calculateAutonomousLoanDetails,
} from './types';
import { calcDefaultInstallmentDates } from '../../lib/workdays';

const STEPS = [
  { id: 1, label: 'Simulação' },
  { id: 2, label: 'Dados' },
  { id: 3, label: 'Endereços' },
  { id: 4, label: 'Contatos' },
  { id: 5, label: 'Agenda' },
  { id: 6, label: 'Documentos' },
  { id: 7, label: 'Revisão' },
];

function initData(
  existingClient: any,
  defaultRuta: number,
  clientType: ClientType,
  previousContract?: any
): ManualRegistrationData {
  const docCategories = clientType === 'autonomous' ? AUTONOMOUS_DOCUMENT_CATEGORIES : DOCUMENT_CATEGORIES;

  const contacts = previousContract?.relative_phones?.length
    ? previousContract.relative_phones.map((rp: any) => ({
        nome: rp.nome || '',
        telefone: rp.numero || '',
        relacao: rp.relacao || '',
        observacao: rp.observacao || '',
        photoFile: null,
        photoUrl: null,
        conversationFile: null,
        conversationUrl: null,
      }))
    : [emptyContact()];

  const enderecoResidencial = previousContract
    ? parseAddress(previousContract.address, previousContract.residential_complement)
    : emptyAddress();

  const enderecoComercial = previousContract
    ? parseAddress(previousContract.business_address, previousContract.commercial_complement)
    : emptyAddress();

  return {
    clientType,
    loan: {
      amount: '',
      installments: clientType === 'autonomous' ? '' : '1',
      interestRate: clientType === 'autonomous' ? '10' : '70',
      isCustom: clientType === 'autonomous',
      installmentDates: clientType === 'autonomous' ? [] : calcDefaultInstallmentDates(1, false),
      ruta: defaultRuta,
    },
    personal: {
      name: existingClient?.client_name || '',
      cpf: existingClient?.cpf || '',
      phone: existingClient?.phone || '',
      pixKey: existingClient?.pix_key || '',
      bank: existingClient?.bank || '',
      serviceEntryTime: existingClient?.service_entry_time || '',
      serviceExitTime: existingClient?.service_exit_time || '',
    },
    enderecoResidencial,
    enderecoComercial,
    contacts,
    documents: docCategories.map(cat => ({ ...cat, files: [], urls: [], uploading: false })),
    declarationConfirmed: false,
  };
}

// ─── Type selection screen ────────────────────────────────────────────────────

function ClientTypeSelection({ onSelect, onBack, onResume }: { onSelect: (t: ClientType) => void; onBack: () => void; onResume: (d: ContractDraft) => void }) {
  return (
    <div className="min-h-screen bg-gray-50 p-4 lg:p-6">
      <div className="max-w-2xl mx-auto">
        <button onClick={onBack} className="text-gray-500 hover:text-gray-700 text-sm mb-6 block">
          ← Voltar
        </button>

        <h1 className="text-xl lg:text-2xl font-bold text-gray-800 mb-2">Novo Contrato</h1>
        <p className="text-gray-500 mb-8 text-sm">Selecione o tipo de cliente para continuar.</p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
          <button
            onClick={() => onSelect('clt')}
            className="bg-white rounded-xl shadow-md border-2 border-transparent hover:border-blue-500 hover:shadow-lg transition-all p-6 text-left group"
          >
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors">
              <HardHat className="w-6 h-6 text-blue-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">CLT</h2>
            <p className="text-sm text-gray-500">
              Cliente com vínculo empregatício formal. Fluxo padrão com holerites e carteira de trabalho.
            </p>
          </button>

          <button
            onClick={() => onSelect('autonomous')}
            className="bg-white rounded-xl shadow-md border-2 border-transparent hover:border-emerald-500 hover:shadow-lg transition-all p-6 text-left group"
          >
            <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-emerald-200 transition-colors">
              <Briefcase className="w-6 h-6 text-emerald-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Autônomo</h2>
            <p className="text-sm text-gray-500">
              Cliente autônomo ou com negócio próprio. Extrato bancário e condições personalizadas.
            </p>
          </button>
        </div>

        <DraftsList onResume={onResume} />
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function LoanSimulation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [clientType, setClientType] = useState<ClientType | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [data, setData] = useState<ManualRegistrationData | null>(null);
  const [prefilled, setPrefilled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [contractTempId, setContractTempId] = useState<string>(() => crypto.randomUUID());
  const [draftId, setDraftId] = useState<string | null>(null);
  const [draftStatus, setDraftStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const liveDocuments = useRef<UploadedDocument[] | null>(null);
  const handleDocumentsChange = useCallback((docs: UploadedDocument[]) => { liveDocuments.current = docs; }, []);
  const [aiReview, setAiReview] = useState<AiReviewState | null>(null);
  const [aiJustification, setAiJustification] = useState('');
  const [statementCheck, setStatementCheck] = useState<StatementCheckState>({ phase: 'idle', files: [] });
  const submittingLock = useRef(false);

  const handleAiReviewChange = (next: AiReviewState) => {
    setAiReview(prev => (next.phase !== 'running' && prev && prev.signature !== next.signature ? prev : next));
    if (next.phase === 'running') setAiJustification('');
  };

  const nextStep = () => { setDraftStatus('idle'); setCurrentStep(prev => Math.min(prev + 1, STEPS.length)); };
  const prevStep = () => { setDraftStatus('idle'); setCurrentStep(prev => Math.max(prev - 1, 1)); };

  const handleSelectType = (type: ClientType) => {
    setClientType(type);
    setPrefilled(false);
    setData(initData(location.state?.existingClient, user?.ruta || 1, type));
    setCurrentStep(1);
    setContractTempId(crypto.randomUUID());
    setDraftId(null);
    setDraftStatus('idle');
    setAiReview(null);
    setStatementCheck({ phase: 'idle', files: [] });
  };

  const handleResumeDraft = (draft: ContractDraft) => {
    setClientType(draft.client_type);
    setPrefilled(draft.prefilled);
    setData(deserializeDraft(draft));
    setCurrentStep(Math.min(Math.max(draft.current_step, 1), STEPS.length));
    setContractTempId(draft.contract_temp_id);
    setDraftId(draft.id);
    setDraftStatus('idle');
    setAiReview(null);
    setStatementCheck({ phase: 'idle', files: [] });
  };

  const handleSaveDraft = async () => {
    if (!data || draftStatus === 'saving') return;
    const snapshot = currentStep === 6 && liveDocuments.current ? { ...data, documents: liveDocuments.current } : data;
    setData(snapshot);
    setDraftStatus('saving');
    try {
      const id = await saveDraft({ id: draftId, data: snapshot, step: currentStep, contractTempId, prefilled });
      setDraftId(id);
      setDraftStatus('saved');
    } catch (err) {
      console.error('Error saving draft:', err);
      setDraftStatus('error');
    }
  };

  const handleClientFound = (contract: any) => {
    setPrefilled(true);
    setData(prev => {
      if (!prev) return prev;
      const fresh = initData(
        { client_name: contract.client_name, cpf: prev.personal.cpf, phone: contract.phone, pix_key: contract.pix_key, bank: contract.bank, service_entry_time: contract.service_entry_time, service_exit_time: contract.service_exit_time },
        user?.ruta || 1,
        prev.clientType,
        contract
      );
      // preserve loan data already filled in step 1
      return { ...fresh, loan: prev.loan };
    });
  };

  const handleSubmit = async () => {
    if (submittingLock.current) return;
    submittingLock.current = true;

    if (!data || !user?.unit_id) {
      setSubmitError('Usuário não está associado a nenhuma unidade.');
      submittingLock.current = false;
      return;
    }

    const loanAmount = parseFloat(data.loan.amount);
    if (!loanAmount || loanAmount <= 0) {
      setSubmitError('Valor do empréstimo inválido. Volte à etapa de simulação e preencha o valor.');
      submittingLock.current = false;
      return;
    }
    if (!data.loan.installmentDates.length) {
      setSubmitError('Datas das parcelas não definidas. Volte à etapa de agenda.');
      submittingLock.current = false;
      return;
    }

    // Re-verify pending balances before creating contract (cross-unit via RPC)
    const cpfDigits = data.personal.cpf.replace(/\D/g, '');
    const { data: balances } = await supabase
      .rpc('check_client_pending_balance', { p_cpf: cpfDigits });

    const pendingBalance = (balances || []).reduce((sum: number, b: any) => sum + Number(b.balance), 0);

    if (pendingBalance > 0) {
      setSubmitError(`Este cliente possui R$ ${pendingBalance.toFixed(2).replace('.', ',')} em aberto. Não é possível criar um novo contrato.`);
      submittingLock.current = false;
      return;
    }

    setSubmitError(null);
    setIsSubmitting(true);

    try {
      const calcDetails = data.clientType === 'autonomous'
        ? calculateAutonomousLoanDetails(data.loan.amount, data.loan.installments, data.loan.interestRate)
        : calculateLoanDetails(data.loan.amount, data.loan.installments, data.loan.interestRate);

      const { totalAmount, installmentAmount } = calcDetails;

      const fmt = (a: typeof data.enderecoResidencial) =>
        `${a.logradouro}, ${a.numero}${a.complemento ? `, ${a.complemento}` : ''} - ${a.bairro}, ${a.cidade}/${a.estado} - CEP: ${a.cep}`;

      const { data: contract, error: contractError } = await supabase
        .from('contracts')
        .insert({
          client_name: data.personal.name,
          cpf: data.personal.cpf.replace(/\D/g, ''),
          phone: data.personal.phone.replace(/\D/g, ''),
          relative_phones: data.contacts.map(c => ({
            numero: c.telefone,
            relacao: c.relacao,
            nome: c.nome,
            observacao: c.observacao,
          })),
          address: fmt(data.enderecoResidencial),
          business_address: fmt(data.enderecoComercial),
          pix_key: data.personal.pixKey,
          bank: data.personal.bank,
          service_entry_time: data.personal.serviceEntryTime,
          service_exit_time: data.personal.serviceExitTime,
          residential_complement: data.enderecoResidencial.complemento,
          commercial_complement: data.enderecoComercial.complemento,
          seller_id: user.id,
          unit_id: user.unit_id,
          loan_amount: parseFloat(data.loan.amount),
          installments: parseInt(data.loan.installments),
          interest_rate: parseFloat(data.loan.interestRate),
          total_amount: totalAmount,
          ruta: data.loan.ruta,
          client_type: data.clientType,
        })
        .select()
        .single();

      if (contractError) throw contractError;

      const installmentInserts = data.loan.installmentDates.map(date => ({
        contract_id: contract.id,
        unit_id: user.unit_id,
        due_date: date.toISOString().split('T')[0],
        amount: installmentAmount,
        status: 'pending',
      }));

      const { error: installmentsError } = await supabase.from('installments').insert(installmentInserts);
      if (installmentsError) throw installmentsError;

      for (const doc of data.documents) {
        for (let fi = 0; fi < doc.urls.length; fi++) {
          await supabase.from('contract_files').insert({
            contract_id: contract.id,
            unit_id: user.unit_id,
            file_name: doc.files[fi]?.name || `${doc.category}-${fi + 1}`,
            file_type: doc.files[fi]?.type || 'image',
            file_url: doc.urls[fi],
          });
        }
      }

      for (let i = 0; i < data.contacts.length; i++) {
        const c = data.contacts[i];
        if (c.photoUrl) {
          await supabase.from('contract_files').insert({
            contract_id: contract.id,
            unit_id: user.unit_id,
            file_name: `contato-${i + 1}-foto`,
            file_type: 'image',
            file_url: c.photoUrl,
          });
        }
        if (c.conversationUrl) {
          await supabase.from('contract_files').insert({
            contract_id: contract.id,
            unit_id: user.unit_id,
            file_name: `contato-${i + 1}-conversa`,
            file_type: 'image',
            file_url: c.conversationUrl,
          });
        }
      }

      const reviewId = data.clientType === 'clt' && aiReview && aiReview.phase !== 'running' ? aiReview.reviewId : null;
      if (reviewId) {
        const { error: linkError } = await supabase.rpc('link_contract_ai_review', {
          p_review_id: reviewId,
          p_contract_id: contract.id,
          p_justification: aiJustification,
        });
        if (linkError) console.error('Error linking AI review:', linkError);
      }

      // Link lead to this contract if one exists for this CPF
      await supabase
        .from('leads')
        .update({ contract_id: contract.id, status: 'awaiting_review' })
        .eq('cpf', cpfDigits)
        .eq('unit_id', user.unit_id)
        .eq('seller_id', user.id)
        .eq('status', 'in_contact');

      if (draftId) await deleteDraft(draftId).catch(err => console.error('Error deleting draft:', err));

      navigate('/dashboard/my-contracts');
    } catch (err) {
      console.error('Error creating contract:', err);
      setSubmitError('Erro ao criar contrato. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
      submittingLock.current = false;
    }
  };

  // ── Type not yet selected ─────────────────────────────────────────────────

  if (!clientType || !data) {
    return (
      <ClientTypeSelection
        onSelect={handleSelectType}
        onBack={() => navigate(-1)}
        onResume={handleResumeDraft}
      />
    );
  }

  // ── Wizard ────────────────────────────────────────────────────────────────

  const stepTitle = clientType === 'autonomous' ? 'Autônomo' : 'CLT';

  return (
    <div className="min-h-screen bg-gray-50">
      <StepHeader steps={STEPS} currentStep={currentStep} title={`Novo Contrato — ${stepTitle}`} />

      <div className="max-w-2xl mx-auto px-4 py-6 pb-36">
        {currentStep === 1 && clientType === 'clt' && (
          <SimulationStep
            data={data.loan}
            statementCheck={
              <StatementCheckCard contractTempId={contractTempId} state={statementCheck} onChange={setStatementCheck} />
            }
            onComplete={(loan) => {
              setData(prev => prev ? { ...prev, loan } : prev);
              nextStep();
            }}
          />
        )}

        {currentStep === 1 && clientType === 'autonomous' && (
          <AutonomousSimulationStep
            data={data.loan}
            onComplete={(loan) => {
              setData(prev => prev ? { ...prev, loan } : prev);
              nextStep();
            }}
          />
        )}

        {currentStep === 2 && (
          <ClientDataStep
            data={data.personal}
            clientType={data.clientType}
            onComplete={(personal) => {
              setData(prev => prev ? { ...prev, personal } : prev);
              nextStep();
            }}
            onClientFound={handleClientFound}
          />
        )}

        {currentStep === 3 && (
          <AddressesStep
            enderecoResidencial={data.enderecoResidencial}
            enderecoComercial={data.enderecoComercial}
            prefilled={prefilled}
            onComplete={({ enderecoResidencial, enderecoComercial }) => {
              setData(prev => prev ? { ...prev, enderecoResidencial, enderecoComercial } : prev);
              nextStep();
            }}
          />
        )}

        {currentStep === 4 && (
          <ContactsStep
            contacts={data.contacts}
            clientPhone={data.personal.phone}
            clientCpf={data.personal.cpf}
            contractTempId={contractTempId}
            prefilled={prefilled}
            onComplete={(contacts) => {
              setData(prev => prev ? { ...prev, contacts } : prev);
              nextStep();
            }}
          />
        )}

        {currentStep === 5 && (
          <ScheduleStep
            key={`schedule-${data.loan.installments}-${data.loan.installmentDates.length}`}
            loan={data.loan}
            sellerRuta={user?.ruta || 1}
            onComplete={(loan) => {
              setData(prev => prev ? { ...prev, loan } : prev);
              nextStep();
            }}
          />
        )}

        {currentStep === 6 && (
          <DocumentsStep
            documents={data.documents}
            contractTempId={contractTempId}
            clientType={data.clientType}
            onChange={handleDocumentsChange}
            onComplete={(documents) => {
              setData(prev => prev ? { ...prev, documents } : prev);
              nextStep();
            }}
          />
        )}

        {currentStep === 7 && (
          <ReviewStep
            data={data}
            contractTempId={contractTempId}
            isSubmitting={isSubmitting}
            error={submitError}
            aiReview={aiReview}
            onAiReviewChange={handleAiReviewChange}
            justification={aiJustification}
            onJustificationChange={setAiJustification}
            onEditDocuments={() => setCurrentStep(6)}
            onConfirm={(declared) => {
              if (!declared) return;
              setData(prev => prev ? { ...prev, declarationConfirmed: true } : prev);
              handleSubmit();
            }}
          />
        )}
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-4 py-3 z-10">
        <div className="max-w-2xl mx-auto">
          {draftStatus === 'saved' && (
            <div className="flex items-center justify-between gap-2 mb-2 text-xs text-emerald-700">
              <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> Rascunho salvo. Você pode concluir depois.</span>
              <button onClick={() => setClientType(null)} className="font-semibold underline underline-offset-2 hover:text-emerald-900">
                Ver rascunhos
              </button>
            </div>
          )}
          {draftStatus === 'error' && (
            <p className="mb-2 text-xs text-red-600">Não foi possível salvar o rascunho. Tente novamente.</p>
          )}
          <div className="flex gap-2">
            {currentStep < 7 && (
              <button
                onClick={currentStep === 1 ? () => setClientType(null) : prevStep}
                className="flex-1 py-3 border-2 border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors text-sm"
              >
                {currentStep === 1 ? '← Voltar ao início' : '← Etapa anterior'}
              </button>
            )}
            <button
              onClick={handleSaveDraft}
              disabled={draftStatus === 'saving' || isSubmitting}
              className="flex-1 flex items-center justify-center gap-2 py-3 border-2 border-amber-200 bg-amber-50 text-amber-800 rounded-xl font-medium hover:bg-amber-100 transition-colors text-sm disabled:opacity-50"
            >
              {draftStatus === 'saving' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {draftStatus === 'saving' ? 'Salvando...' : draftId ? 'Atualizar rascunho' : 'Salvar rascunho'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
